import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mlm/ui';
import { acceptTerms, getMe, getTerms } from '../lib/api';

/**
 * BF1 re-acceptance gate. When the tenant bumps the Terms version past what the
 * member previously accepted (`/me` returns `termsReacceptanceRequired`), this
 * blocks the authed shell with a modal until they re-accept. In normal
 * operation (no version bump) it renders nothing.
 */
export function TermsReacceptGate() {
  const qc = useQueryClient();
  const meQ = useQuery({ queryKey: ['me-terms'], queryFn: getMe });
  const required = !!meQ.data?.termsReacceptanceRequired;
  const termsQ = useQuery({ queryKey: ['terms'], queryFn: getTerms, enabled: required });
  const [busy, setBusy] = useState(false);
  const [showFull, setShowFull] = useState(false);

  if (!required) return null;
  const terms = termsQ.data;

  async function onAccept() {
    setBusy(true);
    try {
      await acceptTerms();
      await qc.invalidateQueries({ queryKey: ['me-terms'] });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Updated terms"
    >
      <div className="w-full max-w-lg rounded-lg border bg-card p-6 shadow-elevated">
        <h2 className="text-h2">We have updated our Terms</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Please review and accept the latest Terms of Service and Privacy Policy to continue.
        </p>
        {showFull && terms && (
          <div className="mt-4 max-h-[50vh] overflow-y-auto rounded-md border bg-background p-4 text-sm text-muted-foreground">
            <h3 className="text-h3">Terms of Service</h3>
            <div className="mt-1 whitespace-pre-wrap">
              {terms.termsText || 'No terms have been published yet.'}
            </div>
            <h3 className="text-h3 mt-4">Privacy Policy</h3>
            <div className="mt-1 whitespace-pre-wrap">
              {terms.privacyText || 'No privacy policy has been published yet.'}
            </div>
          </div>
        )}
        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setShowFull((v) => !v)}
            className="text-xs text-primary underline-offset-2 hover:underline"
          >
            {showFull ? 'Hide' : 'Read the full terms'}
          </button>
          <Button size="sm" disabled={busy} onClick={onAccept}>
            {busy ? 'Saving...' : 'I agree, continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
